import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import profilesAPI from '@/api/profiles';
import { EditProfileForm } from '@/components/EditProfilePage/Form';
import {
  WorkingProfileForm,
  WorkingProfileFormSchema,
} from '@/components/EditProfilePage/schema';
import Layout from '@/components/Layout';
import LoadingAnimation from '@/components/LoadingAnimation';
import COLORS from '@/constants/colors';
import MESSAGES from '@/constants/messages-en';
import useAuth from '@/hooks/useAuth';
import { PreviewProfileProvider } from '@/hooks/usePreviewProfile';
import { IOrganisationSettings } from '@/types/IOrganisation';
import { ProfileSettingsSchema } from '@/types/IProfile';

type ProfileSettings = z.infer<typeof ProfileSettingsSchema>;

// Helper function to map organization settings to profile settings format
function mapOrganizationSettingsToProfileSettings(
  orgSettings: IOrganisationSettings,
): ProfileSettings {
  return {
    logo: orgSettings.company_logo || null,
    logo_file_id: orgSettings.company_logo_file_id || null,
    bg_color: orgSettings.bg_color,
    text_color: orgSettings.text_color,
    button_bg_color: orgSettings.button_bg_color,
    button_text_color: orgSettings.button_text_color,
  };
}

// Static default profile settings used as fallback when organization settings are unavailable
const DEFAULT_PROFILE_SETTINGS = {
  logo: null,
  logo_file_id: null,
  bg_color: COLORS.organisation.defaultProfile.backgroundColor,
  text_color: COLORS.organisation.defaultProfile.textColor,
  button_bg_color: COLORS.organisation.defaultProfile.buttonBackgroundColor,
  button_text_color: COLORS.organisation.defaultProfile.buttonTextColor,
} as const;

export default function EditProfilePage() {
  const location = useLocation();
  const [, , profileIDParam] = location.pathname.split('/');
  const parsedProfileID = parseInt(profileIDParam, 10);

  const { orgID } = useAuth();

  // Validate profileID is a valid positive integer
  if (isNaN(parsedProfileID) || parsedProfileID <= 0) {
    return (
      <Layout pageName="Edit Profile">
        <div role="alert" className="flex items-center justify-center h-full">
          <p className="text-red-600">Invalid profile ID</p>
        </div>
      </Layout>
    );
  }

  const {
    data: profile,
    isLoading: isLoadingProfile,
    isError: isErrorProfile,
  } = useQuery({
    queryKey: ['showProfile', parsedProfileID],
    queryFn: async () => {
      if (orgID === undefined) {
        return;
      }
      const { data } = await profilesAPI.showProfile(orgID, parsedProfileID);
      return data.data;
    },
    enabled: orgID !== undefined,
    refetchOnWindowFocus: false,
  });

  const profileHash = profile?.profile_hash;

  const {
    data: previewProfile,
    isLoading: isLoadingPreviewProfile,
    isError: isErrorPreviewProfile,
  } = useQuery({
    queryKey: ['showProfileWithHashID', profileHash],
    queryFn: async () => {
      if (!profileHash) {
        return;
      }
      const { data } = await profilesAPI.showProfileWithHashID(
        profileHash,
        true, // bypass_analytics=true
      );
      return data.data;
    },
    enabled: !!profileHash,
    refetchOnWindowFocus: false,
  });

  const methods = useForm<WorkingProfileForm>({
    resolver: zodResolver(WorkingProfileFormSchema),
  });

  // Track whether initial form load has completed to prevent re-initialization
  const hasInitialized = useRef(false);

  // Reset hasInitialized when profileID changes (e.g., navigating to different profile)
  useEffect(() => {
    hasInitialized.current = false;
  }, [parsedProfileID]);

  // Extract reset for clearer dependencies
  const { reset } = methods;

  useEffect(() => {
    // Only initialize when we have all the data we're waiting for
    // This prevents the race condition where the form gets reset twice
    const hasAllRequiredData =
      profile && (!profileHash || previewProfile !== undefined);

    // Only initialize once on initial load, never reset after user starts editing
    if (hasAllRequiredData && !hasInitialized.current) {
      // Mark as initialized BEFORE reset to prevent race condition
      hasInitialized.current = true;

      // Use preview profile settings as fallback, or static default settings if unavailable
      const defaultSettings = previewProfile?.settings
        ? mapOrganizationSettingsToProfileSettings(previewProfile.settings)
        : DEFAULT_PROFILE_SETTINGS;

      reset({
        additional_information: profile.additional_information,
        city: profile.city,
        company_name: profile.company_name,
        company_phone_number: profile.company_phone_number,
        company_website: profile.company_website,
        country: profile.country,
        contact_exchange_enabled: profile.contact_exchange_enabled,
        department: profile.department,
        email: profile.email,
        extra_company_phone_numbers: profile.extra_company_phone_numbers,
        first_name: profile.first_name,
        job_title: profile.job_title,
        last_name: profile.last_name,
        middle_name: profile.middle_name,
        mobile_number: profile.mobile_number,
        note: profile.note,
        photo: profile.photo,
        post_code: profile.post_code,
        profile_file_links: profile.profile_file_links.map(link => ({
          status: 'complete',
          ...link,
        })),
        profile_quick_links: profile.profile_quick_links,
        profile_social_links: profile.profile_social_links,
        pronouns: profile.pronouns,
        settings: profile.settings ?? defaultSettings,
        settings_level: profile.settings_level,
        state: profile.state,
        street_address: profile.street_address,
      });
    }
  }, [profile, profileHash, previewProfile, reset]);

  // Show loading while queries are in progress
  if (isLoadingProfile || (profileHash && isLoadingPreviewProfile)) {
    return (
      <Layout pageName="Edit Profile">
        <div className="flex items-center justify-center h-full">
          <LoadingAnimation className="w-16 h-16 mx-auto" />
        </div>
      </Layout>
    );
  }

  // Handle errors
  if (isErrorProfile || isErrorPreviewProfile) {
    return (
      <Layout pageName="Edit Profile">
        <div role="alert" className="flex items-center justify-center h-full">
          <p className="text-red-600">{MESSAGES.error.generic}</p>
        </div>
      </Layout>
    );
  }

  return (
    <PreviewProfileProvider previewProfile={previewProfile}>
      <FormProvider {...methods}>
        <Layout pageName="Edit Profile" className="bg-gray-50">
          <EditProfileForm />
        </Layout>
      </FormProvider>
    </PreviewProfileProvider>
  );
}
